import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Activity, Filter, TrendingUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import LogoLoader from '@/components/LogoLoader';
import { formatDisplayDateTime } from '@/utils/date';

const ActivityLogs = () => {
  const { token, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('all');
  const [filterEntity, setFilterEntity] = useState('all');

  useEffect(() => {
    if (user?.role === 'Admin') {
      loadData();
    }
  }, [filterAction, filterEntity]);

  const loadData = async () => {
    setLoading(true);
    try {
      let url = '/activity-logs?limit=100';
      if (filterAction !== 'all') url += `&action=${filterAction}`;
      if (filterEntity !== 'all') url += `&entity_type=${filterEntity}`;

      const [logsData, statsData] = await Promise.all([
        api.get(url, token),
        api.get('/activity-logs/stats', token)
      ]);
      
      setLogs(logsData.logs);
      setStats(statsData);
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  if (user?.role !== 'Admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only administrators can view activity logs.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return <LogoLoader message="Loading activity logs..." />;
  }

  const getActionColor = (action) => {
    const colors = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      login: 'bg-purple-100 text-purple-800',
      logout: 'bg-gray-100 text-gray-800',
      approve: 'bg-yellow-100 text-yellow-800',
      generate: 'bg-indigo-100 text-indigo-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Activity Logs</h1>
        <p className="text-gray-600 mt-1">Audit trail of all system activities</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Activities</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {stats.by_action.reduce((sum, item) => sum + item.count, 0)}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-utis-forest" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Most Common Action</p>
                  <p className="text-xl font-bold text-gray-900 mt-2 capitalize">
                    {stats.by_action[0]?.action || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {stats.by_action[0]?.count || 0} times
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-utis-forest" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Users</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {stats.recent_users.length}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-utis-forest" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Activity Breakdown by Action */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>Activity Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.by_action.map((item) => (
                <div key={item.action} className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 capitalize">{item.action}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{item.count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters and Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center">
                <Filter className="w-5 h-5 mr-2 text-utis-forest" />
                Activity Log
              </CardTitle>
              <CardDescription>Detailed audit trail of all system activities</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label>Filter by Action</Label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="logout">Logout</SelectItem>
                  <SelectItem value="approve">Approve</SelectItem>
                  <SelectItem value="generate">Generate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Filter by Entity Type</Label>
              <Select value={filterEntity} onValueChange={setFilterEntity}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="consultant">Consultant</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="invoice">Invoice</SelectItem>
                  <SelectItem value="time_entry">Time Entry</SelectItem>
                  <SelectItem value="expense">Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading activity logs...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No activity logs found</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Entity Type</TableHead>
                    <TableHead>Entity Name</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">
                        {formatDisplayDateTime(log.timestamp, '-')}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{log.user_name}</p>
                          <p className="text-xs text-gray-500">{log.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getActionColor(log.action)}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize">{log.entity_type}</TableCell>
                      <TableCell className="text-sm">{log.entity_name || '-'}</TableCell>
                      <TableCell className="text-xs text-gray-600 max-w-xs truncate">
                        {log.details || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogs;
